import React, { useCallback, useEffect, useState } from 'react'
import { useModal } from 'react-modal-hook'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import axios from 'axios'
import { Buffer } from 'buffer'
import moment from 'moment'

import { Icon, Modal, Spinner } from 'core/components'
import routes from 'core/routes'
import { api } from 'core/services'
import { orulTokenManager } from 'core/services/orul-token-manager'

import { Dialog } from 'modules/console/modals'

import { TRootReducer } from 'store'

import { Button, Empty, List, Reward, Wrapper } from './styles'
import { IRewardResponse, TRewads, TReward, VoucherResponse } from './types'

function RewardsView() {
  const { app, user } = useSelector((state: TRootReducer) => state)

  const [rewards, setRewards] = useState<TRewads>([])
  const [vouchers, setVouchers] = useState<VoucherResponse<TReward>[]>([])
  const [loading, setLoading] = useState(true)
  const [selectedReward, setSelectedReward] = useState<TReward>()

  const getColorByScore = useCallback(
    (score: number) => (score > (user.scores || 0) ? '#ffd166' : 'success'),
    [user.scores],
  )

  const fetchVouchers = useCallback(async () => {
    const accessToken = orulTokenManager.getToken()

    if (!accessToken) {
      toast.warn('Sessão expirada. Por favor, faça login novamente.')
      return
    }

    try {
      const { data } = await axios.get<VoucherResponse<TReward>[]>(
        `${process.env.REACT_APP_API_SWAP_URL}/vouchers`,
        {
          params: {
            provider: '@rhaimes/toty',
            rules: Buffer.from(
              `{"path":["groupByKey"],"equals":"${app.currentOrg?.settings.key}"}`,
              'utf8',
            ).toString('base64'),
          },
          headers: {
            Authorization: `Bearer ${accessToken}`,
          },
        },
      )

      setVouchers(data)
    } catch (error) {
      toast.warn(
        'Não foi possível finalizar sua solicitação, atualize a página e tente novamente.',
      )
    }
  }, [app.currentOrg?.settings.key])

  const generateVoucher = useCallback(
    async (object: any, options = {}) => {
      const accessToken = orulTokenManager.getToken()

      if (!accessToken) {
        toast.warn('Sessão expirada. Por favor, faça login novamente.')
        return
      }

      try {
        const { data } = await axios.post<VoucherResponse<TReward>>(
          `${process.env.REACT_APP_API_SWAP_URL}/vouchers`,
          {
            provider: '@rhaimes/toty',
            issuer: '@toty/app',
            expire: '24 hour',
            rules: {
              groupByKey: app.currentOrg?.settings.key,
            },
            attachIntent: false,
            data: object,
            ...options,
          },
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
          },
        )

        setVouchers([...vouchers, data])
      } catch (error) {
        toast.warn(
          'Não foi possível finalizar sua solicitação, atualize a página e tente novamente.',
        )
        //
      }
    },
    [app.currentOrg?.settings.key, vouchers],
  )

  const [showModalRewardAvailable, hideModalRewardAvailable] = useModal(() => {
    const companies = rewards.find(
      reward => reward.id === selectedReward?.id,
    )?.available_companies

    return (
      <Modal onHide={hideModalRewardAvailable} position="center">
        <Dialog title="Empresas Disponíveis">
          {companies?.length ? (
            <List.Container>
              {companies
                .sort((a, b) => a.name.localeCompare(b.name))
                .map(company => (
                  <List.Item key={company.ref}>{company.name}</List.Item>
                ))}
            </List.Container>
          ) : (
            <Empty
              style={{
                fontSize: '.9rem',
                marginBottom: '30px',
              }}
            >
              Recompensa indisponível!
            </Empty>
          )}
        </Dialog>
      </Modal>
    )
  }, [rewards, selectedReward])

  const [showModalGenerateVoucher, hideModalGenerateVoucher] = useModal(() => {
    const attachIntent = (
      rewards.find(reward => reward.id === selectedReward?.id)
        ?.available_companies || []
    ).some(item => item.swap_intents_rescues_auth)

    const voucher = vouchers.find(item => item.data.id === selectedReward?.id)

    const voucherAlreadyGenerated = voucher?.data.id === selectedReward?.id

    return (
      <Modal onHide={hideModalGenerateVoucher} position="center">
        <Dialog
          title="Voucher"
          message="Ao gerar um voucher, um código é disponibilizado para facilitar o
            resgate no ponto de vendas."
        >
          <div style={{ margin: '25px 0', textAlign: 'center' }}>
            <Reward.Image.Preview
              onError={(e: React.ChangeEvent<HTMLImageElement>) => {
                e.target.src = '/assets/images/no-image.png'
                e.target.style.width = '8rem'
              }}
              src={`${process.env.REACT_APP_CDN}/${selectedReward?.image_url}`}
            />
            <h3>{selectedReward?.name}</h3>
            {voucherAlreadyGenerated ? (
              <div>
                <code>
                  <h2
                    style={{
                      color: 'rgb(255, 107, 40)',
                      marginTop: 15,
                      userSelect: 'all',
                      MozUserSelect: 'all',
                      WebkitUserSelect: 'all',
                    }}
                  >
                    {voucher?.code}
                  </h2>
                </code>
                <br />
                <span>
                  Este código ainda é válido por
                  <br />
                  <strong style={{ display: 'inline-block', padding: 10 }}>
                    {moment(voucher?.expiryAt).diff(moment(), 'hours')} horas e{' '}
                    {moment(voucher?.expiryAt).diff(moment(), 'minutes') % 60}{' '}
                    minutos.
                  </strong>
                </span>
              </div>
            ) : (
              <Button
                onClick={event => {
                  const {
                    id,
                    name,
                    value,
                    description,
                    reward_type,
                    reward_type_name,
                    debited_value,
                  } = selectedReward as TReward

                  generateVoucher(
                    {
                      id,
                      name,
                      value,
                      description,
                      reward_type,
                      reward_type_name,
                      debited_value,
                    },
                    { attachIntent },
                  )

                  event.currentTarget.innerHTML = 'Gerando Voucher...'
                  event.currentTarget.disabled = true
                }}
                color="info"
              >
                Gerar Voucher
              </Button>
            )}
          </div>
          <p style={{ fontSize: 11 }}>
            * O voucher não é garantia de resgate do produto no ponto de vendas,
            já que no ato do resgate de recompensa, é necessário ter pontos
            suficientes e que o produto esteja disponível no estabelecimento.
          </p>
        </Dialog>
      </Modal>
    )
  }, [rewards, selectedReward, generateVoucher])

  const showRewardInfo = useCallback(
    (reward: TReward) => {
      const qtdAvailable = Math.floor((user.scores || 0) / reward.debited_value)
      const requiredScore = reward.debited_value - (user.scores || 0)

      return (user.scores || 0) >= reward.debited_value ? (
        <Reward.Details.Info color="success">
          {qtdAvailable > 1
            ? `Até ${qtdAvailable} resgates disponíveis`
            : '1 resgate disponível'}
        </Reward.Details.Info>
      ) : (
        <Reward.Details.Info>
          <span style={{ color: '#ffd166' }}>
            {`+${requiredScore} ponto${requiredScore > 1 ? 's' : ''} `}
          </span>
          para resgatar esta recompensa
        </Reward.Details.Info>
      )
    },
    [user.scores],
  )

  const getRewards = useCallback(async () => {
    api
      .get(`${routes.api.rewards.find}?per_organization=true`, {
        headers: {
          'X-App-Token': app.currentOrg?.token as string,
        },
      })
      .then(({ data }: IRewardResponse) => {
        if (data) {
          setRewards(
            data
              .map((reward: TReward) => reward)
              .sort((a, b) => a.debited_value - b.debited_value),
          )
        }

        setLoading(false)
      })
      .catch(() => {
        toast.error(
          'Erro ao buscar as recompensas. Atualize a página e tente novamente',
        )
        setLoading(false)
      })
  }, [app.currentOrg?.token])

  useEffect(() => {
    getRewards()
    fetchVouchers()
  }, [getRewards, fetchVouchers])

  if (loading) {
    return <Spinner />
  }

  return (
    <Wrapper>
      {rewards.length ? (
        rewards.map(reward => (
          <Reward.Container key={reward.id}>
            <Reward.Score.Container>
              <Icon
                icon="medal"
                color={getColorByScore(reward.debited_value)}
                size="72px"
              />

              <Reward.Score.Overlap>
                <Reward.Score.Span>{reward.debited_value}</Reward.Score.Span>
                <Reward.Score.Label>
                  {`ponto${reward.debited_value > 1 ? 's' : ''}`}
                </Reward.Score.Label>
              </Reward.Score.Overlap>
            </Reward.Score.Container>

            <Reward.Image.Cover>
              <Reward.Image.Preview
                onError={(e: React.ChangeEvent<HTMLImageElement>) => {
                  e.target.src = '/assets/images/no-image.png'
                  e.target.style.width = '8rem'
                }}
                src={`${process.env.REACT_APP_CDN}/${reward.image_url}`}
              />
            </Reward.Image.Cover>

            <Reward.Details.Container>
              <Reward.Details.Title>{reward.name}</Reward.Details.Title>

              {showRewardInfo(reward)}

              {(user.scores || 0) >= reward.debited_value && (
                <Button
                  onClick={() => {
                    setSelectedReward(reward)
                    showModalGenerateVoucher()
                  }}
                  color="info"
                >
                  Gerar Voucher
                </Button>
              )}

              <Button
                onClick={() => {
                  setSelectedReward(reward)
                  showModalRewardAvailable()
                }}
                color={getColorByScore(reward.debited_value)}
              >
                Verificar Disponibilidade
              </Button>
            </Reward.Details.Container>
          </Reward.Container>
        ))
      ) : (
        <Empty>Nehuma recompensa encontrada.</Empty>
      )}
    </Wrapper>
  )
}

export { RewardsView }
